/**
 * サイドメニューのドットの土台 (`docs/specs/common/shared-components.md`
 * AC-SC-004 / 005 / 034 / 035 / 041 / 042)。
 *
 * ドットの種類ごとに「メニューの path → ドット」を作る側 (例: `autoBidNotOrderedDot.ts`) と、
 * 描画 (グループ・項目) を分け、同じ項目・グループに複数のドットが重なった時の
 * まとめ方 (赤 ＞ 黄、理由は重複を除いて「 / 」区切り) をここ 1 か所で決める。
 * 同一グループ・同一項目に複数種別が重なってもドットは 1 つに集約する
 * (AC-SC-035 ※。Des 判断 #9940)。
 */

/** 重さ。negative = 不整合・緊急 (赤)、warning = 対応待ち・通知 (黄)。 */
export type SideMenuDotSeverity = 'negative' | 'warning'

export type SideMenuDot = {
  severity: SideMenuDotSeverity
  /** 表示文言 (i18n 解決済み)。tooltip と aria-label に出す。 */
  reasons: readonly string[]
}

/** 理由を並べる区切り (design `_navWarnTitle` と同じ)。 */
const REASON_SEPARATOR = ' / '

/**
 * 複数のドットを 1 つにまとめる。重さは negative が勝ち、理由は出現順に重複を除く。
 * ドットが無ければ undefined。
 */
export const mergeSideMenuDots = (
  dots: readonly SideMenuDot[],
): SideMenuDot | undefined => {
  if (dots.length === 0) return undefined
  const severity: SideMenuDotSeverity = dots.some(
    (m) => m.severity === 'negative',
  )
    ? 'negative'
    : 'warning'
  const reasons = [...new Set(dots.flatMap((m) => m.reasons))]
  return { severity, reasons }
}

/** tooltip / aria-label に出す 1 本の文。 */
export const sideMenuDotLabel = (dot: SideMenuDot): string =>
  dot.reasons.join(REASON_SEPARATOR)

/** 「path → ドット」の組を path ごとに 1 つへまとめる。 */
export const collectSideMenuDots = (
  entries: ReadonlyArray<readonly [string, SideMenuDot]>,
): ReadonlyMap<string, SideMenuDot> => {
  const byPath = new Map<string, SideMenuDot[]>()
  for (const [path, dot] of entries) {
    byPath.set(path, [...(byPath.get(path) ?? []), dot])
  }
  const result = new Map<string, SideMenuDot>()
  for (const [path, dots] of byPath) {
    const merged = mergeSideMenuDots(dots)
    if (merged !== undefined) result.set(path, merged)
  }
  return result
}

/** グループのドット = 子の項目 (path) のドットをまとめたもの。 */
export const resolveGroupSideMenuDot = (
  itemPaths: readonly string[],
  dots: ReadonlyMap<string, SideMenuDot>,
): SideMenuDot | undefined =>
  mergeSideMenuDots(
    itemPaths.flatMap((path) => {
      const dot = dots.get(path)
      return dot === undefined ? [] : [dot]
    }),
  )
